import { useEffect, useState } from 'react'
import { X, Copy, Check, ExternalLink, Share2 } from 'lucide-react'
import { buildShareUrl, type SharedRosterConfig, describeSharedPattern } from '@/lib/rosterShare'

interface ShareRosterDialogProps {
  isOpen: boolean
  onClose: () => void
  config: SharedRosterConfig
}

export default function ShareRosterDialog({ isOpen, onClose, config }: ShareRosterDialogProps) {
  const [shareUrl, setShareUrl] = useState<string>('')
  const [copyStatus, setCopyStatus] = useState<'idle' | 'copied' | 'error'>('idle')

  useEffect(() => {
    if (isOpen) {
      setShareUrl(buildShareUrl(config))
      setCopyStatus('idle')
    }
  }, [isOpen])

  useEffect(() => {
    if (!isOpen) return
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [isOpen, onClose])

  if (!isOpen) return null

  const copyShareLink = async () => {
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(shareUrl)
      } else {
        const field = document.createElement('textarea')
        field.value = shareUrl
        field.style.position = 'fixed'
        field.style.opacity = '0'
        document.body.appendChild(field)
        field.select()
        document.execCommand('copy')
        document.body.removeChild(field)
      }
      setCopyStatus('copied')
      setTimeout(() => setCopyStatus('idle'), 2500)
    } catch (error) {
      console.error('Copy failed:', error)
      setCopyStatus('error')
    }
  }

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="share-dialog-title"
    >
      <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 id="share-dialog-title" className="text-xl font-bold text-slate-900 flex items-center gap-2">
            <Share2 className="w-5 h-5 text-orange-500" />
            Share This Roster
          </h3>
          <button
            onClick={onClose}
            className="p-2 hover:bg-slate-100 rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-teal-500"
            aria-label="Close share dialog"
          >
            <X className="w-5 h-5 text-slate-600" />
          </button>
        </div>

        <p className="text-sm text-slate-600 mb-4">
          Anyone with this link opens the calculator with your pattern, start date and duration already filled
          in. No account needed.
        </p>

        <div className="mb-4 p-3 bg-slate-50 border border-slate-200 rounded-lg text-sm text-slate-700 space-y-1">
          <p>
            <span className="font-semibold">Pattern:</span>{' '}
            {config.pattern.name || describeSharedPattern(config.pattern)}
          </p>
          <p>
            <span className="font-semibold">Starts:</span>{' '}
            {new Date(config.startDate).toLocaleDateString('en-US', {
              month: 'long',
              day: 'numeric',
              year: 'numeric',
            })}
          </p>
          <p>
            <span className="font-semibold">Shows:</span> {config.monthsToShow} month
            {config.monthsToShow > 1 ? 's' : ''}
          </p>
        </div>

        <label className="block text-sm font-medium text-slate-700 mb-2" htmlFor="share-link-field">
          Shareable link
        </label>
        <input
          id="share-link-field"
          type="text"
          readOnly
          value={shareUrl}
          onFocus={(e) => e.currentTarget.select()}
          className="w-full px-4 py-3 mb-3 text-sm border border-slate-300 rounded-lg bg-slate-50 text-slate-700 focus:outline-none focus:ring-2 focus:ring-teal-500"
        />

        {copyStatus === 'copied' && (
          <div className="mb-3 p-3 rounded-lg text-sm bg-green-50 text-green-800 border border-green-200" role="status">
            Link copied. Paste it into an email, chat or team notice.
          </div>
        )}
        {copyStatus === 'error' && (
          <div className="mb-3 p-3 rounded-lg text-sm bg-red-50 text-red-800 border border-red-200" role="alert">
            Copying was blocked by the browser. Select the link above and copy it manually.
          </div>
        )}

        <div className="flex flex-wrap gap-3">
          <button
            onClick={copyShareLink}
            className="flex-1 min-w-[140px] px-4 py-3 bg-teal-500 text-white rounded-lg hover:bg-teal-600 transition-colors font-medium flex items-center justify-center gap-2 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-teal-500"
          >
            {copyStatus === 'copied' ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
            {copyStatus === 'copied' ? 'Copied!' : 'Copy link'}
          </button>
          <a
            href={shareUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="flex-1 min-w-[140px] px-4 py-3 bg-white border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-50 transition-colors font-medium flex items-center justify-center gap-2 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-slate-500"
          >
            <ExternalLink className="w-4 h-4" />
            Preview
          </a>
        </div>

        <p className="text-xs text-slate-500 mt-4">
          The link carries only the pattern, start date and duration — no personal or saved data.
        </p>
      </div>
    </div>
  )
}
